import React, { useState, useEffect, useMemo, useRef } from 'react'
import { message } from 'antd'
import { useTranslation } from 'react-i18next'
import ClientsSection from './ClientsSection'
import BrandsSection from './BrandsSection'
import AdsSection from './AdsSection'
import AdLibraryModals from './modals/AdLibraryModals'
import useAdLibraryModals from '../hooks/useAdLibraryModals'
import { resolveDefaultTag } from '../utils/folderTree'
import { contentTypeParams } from '../../../../services/contents'
import AdLibraryComponentHolder from '../styles/adLibraryComponentStyle'

// Domain state (ads/folders/selection) + actions come from the isolated Redux
// store via AdLibraryContainer, fed by the real API. Modal visibility is
// ephemeral UI state and stays local (useAdLibraryModals), as do the
// client/brand selections (which are visual — the contents API has no
// brand/client filter, so listing is scoped by company/user).
const AdLibraryComponent = ({
  userId,
  companyId,
  companyType,
  isSuperUser = false,
  ads,
  adsTotal,
  folders,
  selectedIds,
  selectedTag,
  clients,
  loadingClients,
  categories = [],
  loadingCategories = false,
  loadingAds,
  loadingFolders,
  uploading,
  uploadProgress = [],
  folderSubmitting = false,
  folderError = null,
  toggleSelect,
  clearSelection,
  selectAll,
  getTags,
  getContents,
  setSelectedTag,
  createFolder,
  renameFolder,
  deleteFolders,
  uploadAds,
  renameAd,
  deleteAds,
  getClients,
  getCategories,
}) => {
  const { t } = useTranslation()
  const [selectedClient, setSelectedClient] = useState(null)
  const [selectedBrand, setSelectedBrand] = useState(null)

  const [pendingDefaultTag, setPendingDefaultTag] = useState(false)

  // Settled (debounced) search terms emitted by each section's SearchInput,
  // which owns the input + debounce. These drive the server-side `search` param.
  const [clientSearch, setClientSearch] = useState('')
  const [adSearch, setAdSearch] = useState('')
  // Server-side ad type filter ('image' | 'video' | 'dynamic' | null). null =>
  // every type the user is allowed to see.
  const [adType, setAdType] = useState(null)
  // Server-side ad sort ('name' | 'oldest' | 'newest' | null). Defaults to 'newest'.
  const [adSort, setAdSort] = useState('newest')
  // Current ads page (1-based; server-side, 24 per page). Reset to 1 whenever the
  // listing context changes (folder, brand/client, search, type).
  const [adPage, setAdPage] = useState(1)

  const { modal, payload, open, close } = useAdLibraryModals()

  // Agencies organize their ads by client → brand; media owners don't, so the
  // client/brand sections (and their selection gate) only apply to agencies.
  const isAgency = companyType === 'agency'

  // Brands come embedded in the client (GET /clients → client.brands); there is
  // no separate brands fetch. See docs/contents-flow.md.
  const brands = (selectedClient && selectedClient.brands) || []

  // Advertisers list by company, individual users by their own id.
  const scope = useMemo(
    () => (companyId ? { id: companyId, isCompany: true } : userId ? { id: userId, isCompany: false } : null),
    [companyId, userId],
  )

  // Agencies gate browsing behind a client + brand selection (a UX gate — the
  // listing itself is company/user-scoped). Media owners have no such sections,
  // so they browse directly as soon as the scope is known.
  const canBrowse = isAgency ? !!(selectedClient && selectedBrand) : !!scope

  // Clients (and their embedded brands) only power the agency sections.
  // Re-fetches (server-side filtered) as the debounced client search changes.
  useEffect(() => {
    if (isAgency && userId) getClients(userId, clientSearch)
  }, [isAgency, userId, clientSearch, getClients])

  // Content categories for the upload modal — any ad type can carry one. Fetched
  // the first time the modal opens rather than on mount, since most library
  // visits never upload; once loaded it stays in the store. The ref caps it at
  // one request per modal open, so a failed fetch can't retry in a loop (the
  // field is optional, so an empty catalog degrades to no options).
  const categoriesRequested = useRef(false)
  useEffect(() => {
    if (modal !== 'uploadAd') {
      categoriesRequested.current = false
      return
    }
    if (categoriesRequested.current || categories.length) return
    categoriesRequested.current = true
    getCategories()
  }, [modal, categories.length, getCategories])

  // Fetch folders (content-tags) once a client + brand are selected.
  useEffect(() => {
    if (scope && canBrowse) getTags(scope)
  }, [scope, canBrowse, getTags])

  // Fetch ads once a brand is selected — a folder is NOT required. With no
  // folder selected the contents come unfiltered (no `tag`); selecting a folder
  // refetches filtered by its id. Re-runs whenever the selected folder changes.
  // While a default tag is pending we hold off: the resolution effect below sets
  // selectedTag first, so contents load already filtered (no unfiltered flash).
  useEffect(() => {
    if (scope && canBrowse && !pendingDefaultTag)
      getContents(scope, selectedTag || null, adSearch, contentTypeParams(adType, isSuperUser), adPage, adSort)
  }, [scope, canBrowse, selectedTag, pendingDefaultTag, adSearch, adType, isSuperUser, adPage, adSort, getContents])


  useEffect(() => {
    if (!isAgency || !pendingDefaultTag || loadingFolders || !folders.length) return
    const tag = resolveDefaultTag(folders, selectedClient && selectedClient.id, selectedBrand)
    if (tag != null) setSelectedTag(tag)
    setPendingDefaultTag(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgency, pendingDefaultTag, loadingFolders, folders, selectedClient, selectedBrand])

  const handleSelectClient = (client) => {
    setSelectedClient(client)
    setSelectedBrand(null)
    setSelectedTag(null)
    setPendingDefaultTag(false)
    setAdPage(1)
  }

  const handleSelectBrand = (brandId) => {
    setSelectedBrand(brandId)
    // Reset navigation; the resolution effect will pick the default folder once
    // the tree is available (or stays at root when no brand is selected).
    setSelectedTag(null)
    setPendingDefaultTag(!!brandId)
    setAdPage(1)
  }

  // Searching for a client clears the current selection: the client (so the
  // embedded brands list empties), the brand, and the folder navigation. The
  // user then picks a client from the results to repopulate the brands.
  const handleClientSearch = (value) => {
    setClientSearch(value)
    setSelectedClient(null)
    setSelectedBrand(null)
    setSelectedTag(null)
    setPendingDefaultTag(false)
    setAdPage(1)
  }

  const handleSelectFolder = (folder) => {
    // Navigate into a folder (or back to root with null). The contents effect
    // above performs the (un)filtered refetch when selectedTag changes.
    setSelectedTag(folder ? folder.key : null)
    setAdPage(1)
  }

  // Searching/filtering ads resets to the first page (server-side pagination).
  const handleAdSearch = (value) => {
    setAdSearch(value)
    setAdPage(1)
  }

  const handleAdTypeChange = (type) => {
    setAdType(type)
    setAdPage(1)
  }

  // Changing the sort resets to the first page (server-side pagination).
  const handleAdSortChange = (value) => {
    setAdSort(value || null)
    setAdPage(1)
  }

  // Auto-select the first client once the list loads (its brands come embedded).
  // Skipped while a client search is active, so searching clears the selection
  // and the user picks a result instead of it snapping back to the first match.
  // The brand is NOT auto-selected — the user picks it.
  useEffect(() => {
    if (isAgency && !loadingClients && clients.length && !selectedClient && !clientSearch) {
      handleSelectClient(clients[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAgency, clients, loadingClients, selectedClient, clientSearch])

  // Per-file upload toasts (antd message). Each file gets its OWN toast: a
  // "uploading <file>…" that is replaced in place (same `key`) by a success or
  // error toast as that file's upload resolves. The per-file keys (by upload
  // index) live in a ref; `processedRef` tracks how many progress entries we've
  // already toasted so the effect only handles new ones.
  const uploadKeysRef = useRef([])
  const processedRef = useRef(0)
  const batchRef = useRef(0)

  const uploadAdsWithToast = (files, meta) => {
    const list = Array.from(files || [])
    // A dynamic ad (DCO) carries no files: it is a single item toasted under the
    // name the user typed.
    const dynamic = meta && meta.dynamic
    const names = dynamic ? [dynamic.name] : list.map((file) => file.name)
    if (!names.length) return
    const batch = (batchRef.current += 1)
    uploadKeysRef.current = names.map((name, i) => ({ key: `upload-${batch}-${i}`, name }))
    processedRef.current = 0
    // One persistent (duration 0) loading toast per file, keyed so it can be
    // replaced by its result later.
    uploadKeysRef.current.forEach(({ key, name }) => {
      message.loading({ content: t('Uploading {{name}}…', { name }), key, duration: 0 })
    })
    uploadAds(list, { ...meta, folderId: (meta && meta.folderId) || selectedTag })
  }

  // Replace each file's loading toast with its result as progress entries arrive.
  useEffect(() => {
    const progress = uploadProgress || []
    if (progress.length <= processedRef.current) return
    for (let i = processedRef.current; i < progress.length; i += 1) {
      const { index, name, ok, status } = progress[i]
      const item = uploadKeysRef.current[index]
      const key = item ? item.key : `upload-result-${index}`
      if (ok) message.success({ content: t('{{name}} uploaded', { name }), key })
      // 403 only reaches here if the client-side role check disagrees with the
      // backend's, but a permissions failure deserves to say so.
      else if (status === 403)
        message.error({ content: t('You do not have permission to upload this type of ad'), key })
      else message.error({ content: t('Could not upload {{name}}', { name }), key })
    }
    processedRef.current = progress.length
  }, [uploadProgress, t])

  const folderWasSubmittingRef = useRef(false)
  useEffect(() => {
    if (folderSubmitting) {
      folderWasSubmittingRef.current = true
      return
    }
    if (!folderWasSubmittingRef.current) return
    folderWasSubmittingRef.current = false
    if (folderError) {
      // The message comes from the backend (e.g. a duplicate-name validation
      // error); t() localizes it when a matching key exists and otherwise falls
      // back to the raw string.
      message.error(t(folderError))
    } else if (modal === 'createFolder' || modal === 'renameFolder') {
      close()
    }
  }, [folderSubmitting, folderError, modal, close, t])

  const actions = {
    createFolder,
    renameFolder,
    deleteFolders,
    // Default the upload tag to the open folder; the modal can override it.
    uploadAds: uploadAdsWithToast,
    renameAd,
    deleteAds,
  }

  return (
    <AdLibraryComponentHolder>
      <h1 className="ad-library-title">{t('Ad library')}</h1>

      {/* Client & brand sections are agency-only; media owners don't use them. */}
      {isAgency && (
        <ClientsSection
          clients={clients}
          loading={loadingClients}
          onSearch={handleClientSearch}
          selectedClient={selectedClient}
          onSelectClient={handleSelectClient}
        />
      )}

      {isAgency && (
        <BrandsSection
          brands={brands}
          loading={loadingClients}
          clientId={selectedClient && selectedClient.id}
          selectedBrand={selectedBrand}
          onSelectBrand={handleSelectBrand}
        />
      )}

      <AdsSection
        isSuperUser={isSuperUser}
        ads={ads}
        adsTotal={adsTotal}
        page={adPage}
        onPageChange={setAdPage}
        folders={folders}
        brands={brands}
        selectedTag={selectedTag}
        canBrowse={canBrowse}
        loadingAds={loadingAds}
        loadingFolders={loadingFolders}
        uploading={uploading}
        onSearch={handleAdSearch}
        onTypeChange={handleAdTypeChange}
        sort={adSort}
        onSortChange={handleAdSortChange}
        onUploadFiles={(files, folderId) =>
          actions.uploadAds(files, folderId ? { folderId } : {})
        }
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onSetSelection={selectAll}
        onClearSelection={clearSelection}
        onDeleteSelected={(ids, folderIds) => open('deleteAds', { ids, folderIds })}
        onSelectFolder={handleSelectFolder}
        openModal={open}
      />

      <AdLibraryModals
        modal={modal}
        payload={payload}
        close={close}
        actions={actions}
        folders={folders}
        selectedTag={selectedTag}
        uploading={uploading}
        folderSubmitting={folderSubmitting}
        categories={categories}
        loadingCategories={loadingCategories}
        isSuperUser={isSuperUser}
      />
    </AdLibraryComponentHolder>
  )
}

export default AdLibraryComponent
